import { EnumTipoDomandaDTOStato } from '@framework/api-client/api/inquiries/schemas/enumTipoDomandaDTOStato.ts';
import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries/schemas/tipoDomandaDTO.ts';
import { type DatiTitolare } from '@framework/api-client/api/inquiries-compile/schemas/datiTitolare.ts';
import { EnumDatiTitolareTipologia } from '@framework/api-client/api/inquiries-compile/schemas/enumDatiTitolareTipologia.ts';
import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { EnumRichiestaDTOTipoIntegrazione } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOTipoIntegrazione.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTOStato.ts';
import { type DatiSessioneUtente } from '@framework/api-client/api/users/schemas/datiSessioneUtente.ts';
import { isWidgetIntegrable } from '@framework/common-web/engine/directives/integrable/IntegrableDirective.type.tsx';
import { CommunicationLogic } from '@framework/common-web/ui/communication/logic/CommunicationLogic.ts';
import { isInquiryReadonlyPreview } from '@framework/common-web/ui/inquiry/isInquiryReadonlyPreview.ts';
import { type Widget } from '@framework/engine/schema/widgets/Widget.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { isEmpty } from 'lodash';

export class SubmissionLogic {
  static canIntegrate(
    inquiry: TipoDomandaDTO | null | undefined,
    submission: RichiestaDTO | null | undefined,
  ): boolean {
    // E possibile integrare (duplicare) la richiesta solo se questa è in stato INVIATA, RICEVUTA
    if (
      submission?.stato != EnumRichiestaDTOStato.INVIATA &&
      submission?.stato != EnumRichiestaDTOStato.RICEVUTA
    )
      return false;

    // e ha il tipo_integrazione_permessa uguale a INTEGRAZIONE_VOLONTARIA o RICHIESTA_INTEGRAZIONE.
    if (submission.tipoIntegrazione == EnumRichiestaDTOTipoIntegrazione.NESSUNA)
      return false;

    // Inoltre solo l’integrazione volontaria non è consentita se il tipo di domanda
    // ha il flag blocca integrazione volontaria uguale a true
    if (
      submission.tipoIntegrazione ==
        EnumRichiestaDTOTipoIntegrazione.INTEGRAZIONE_VOLONTARIA &&
      inquiry?.bloccaIntegrazioneVolontaria == true
    )
      return false;

    return true;
  }

  /**
   * Controlla se una richiesta è in stato di integrazione
   */
  static hasIntegrations(submission: RichiestaDTO | null | undefined): boolean {
    return (
      // 1. La richiesta ha una richiesta originale, quindi è una integrazione
      submission?.richiestaOriginale != null ||
      // 2. La richiesta è in stato INTEGRATA (es. prima richiesta, dopo essere stata integrata, è in stato INTEGRATA)
      submission?.stato == EnumRichiestaDTOStato.INTEGRATA
    );
  }

  /**
   * Controlla se una richiesta ha un numero di procedimento / protocollo.
   * In quel caso nascondiamo il codice della richiesta.
   */
  static getCodes(submission: RichiestaDTO | null | undefined) {
    const hasNumbers =
      !!submission?.numeroProcedimento || !!submission?.numeroProtocollo;
    if (hasNumbers) {
      return {
        protocolNumber: submission?.numeroProtocollo,
        procedureNumber: submission?.numeroProcedimento,
      };
    }

    return {
      submissionCode: submission?.id,
    };
  }

  static cannotSendAsDelegate(
    submission: RichiestaDTO | null | undefined,
    user: DatiSessioneUtente | null | undefined,
  ) {
    return (
      submission?.datiDelega?.permetteInvio == false &&
      user?.utenteLoggato?.idUtente == submission?.idDelegato
    );
  }

  /**
   * Disabilito il caricamento del file firmato
   */
  static cannotSignAsDelegate(
    submission: RichiestaDTO | null | undefined,
    user: DatiSessioneUtente | null | undefined,
  ) {
    return (
      submission?.datiDelega?.permetteFirma === false &&
      user?.utenteLoggato?.idUtente == submission?.idDelegato
    );
  }

  static getMainApplicant(
    submission: RichiestaDTO | null | undefined,
  ): DatiTitolare | null {
    return submission?.datiTitolari?.find(a => a.flTitolarePrincipale) ?? null;
  }

  static getApplicant(submission: RichiestaDTO | null | undefined) {
    if (!submission) return null;
    if (!submission.datiTitolari?.length) return null;

    const mainApplicantData = SubmissionLogic.getMainApplicant(submission);

    // TODO/MERGE verifificare se il `null` può dare problemi
    const mainOwner = SubmissionLogic.getApplicantLabel(mainApplicantData);

    // inseriamo il codice fiscale del titolare principale
    const mainOwnerFiscalCode = mainApplicantData?.soggettoGiuridico
      ? (mainApplicantData?.soggettoGiuridico.codiceFiscale ??
        mainApplicantData?.soggettoGiuridico.partitaIva ??
        '')
      : (mainApplicantData?.codiceFiscale ?? '');

    const others = Array.from(
      new Set(
        submission.datiTitolari
          .filter(
            t => !t.flTitolarePrincipale,
            //&&
            // t.tipologia === EnumDatiTitolareTipologia.TITOLARE,
          )
          .map(t => SubmissionLogic.getApplicantLabel(t)),
      ),
    );

    const multipleApplicants = others.length > 0;

    return { mainOwner, others, multipleApplicants, mainOwnerFiscalCode };
  }

  /**
   * Ottiene l'etichetta del titolare
   */
  static getApplicantLabel(applicant: DatiTitolare | null | undefined) {
    const undefinedApplicant = t(
      'submission-list.unknown-applicant',
      'non identificato',
    );
    if (!applicant) return undefinedApplicant;
    const fiscalNumber = isEmpty(applicant.codiceFiscale?.trim())
      ? undefinedApplicant
      : applicant.codiceFiscale!;

    return isEmpty(applicant.nome?.trim()) || isEmpty(applicant.cognome?.trim())
      ? fiscalNumber
      : (applicant.soggettoGiuridico?.nome ??
          `${applicant.nome ?? ''} ${applicant.cognome ?? ''}`);
  }

  static getSubmitterFullName(submission: RichiestaDTO | null | undefined) {
    const fullName = [
      submission?.nomeRichiedente?.trim(),
      submission?.cognomeRichiedente?.trim(),
    ]
      .filter(name => !isEmpty(name))
      .join(' ');

    return fullName || null;
  }

  /**
   * Controlla se è possibile creare una nuova richiesta.
   * Oltre alle domande attive (o in collaudo), è possibile creare la
   * richiesta per navigare i moduli quando è attiva l'anteprima in
   * sola lettura.
   */
  static canCreateSubmission(inquiry: TipoDomandaDTO | undefined) {
    return (
      inquiry?.stato === EnumTipoDomandaDTOStato.APERTA ||
      inquiry?.inCollaudo === true ||
      isInquiryReadonlyPreview(inquiry)
    );
  }

  static canShowCommunications(
    submissionState: RichiestaDTOStato | null | undefined,
  ) {
    return (
      submissionState !== EnumRichiestaDTOStato.INVIATA &&
      CommunicationLogic.isVisible()
    );
  }

  /**
   * Ritorna vero se è possibile creare, modificare e inviare comunicazioni
   * per la richiesta. Lo storico resta comunque consultabile.
   *
   * Con stato assente (`undefined`/`null`) ritorna vero: scelta permissiva
   * deliberata per evitare che i controlli spariscano durante il caricamento;
   * il backend resta l'autorità e rifiuta comunque le operazioni non
   * consentite.
   */
  static canSendCommunications(
    submissionState: RichiestaDTOStato | null | undefined,
  ) {
    return (
      submissionState !== EnumRichiestaDTOStato.CONCLUSA &&
      submissionState !== EnumRichiestaDTOStato.ANNULLATA &&
      submissionState !== EnumRichiestaDTOStato.ANNULLAMENTO_IN_CORSO
    );
  }

  /**
   * Controlla se è possibile duplicare una richiesta controllando il flag di univocità
   */
  static canDuplicate(submission: RichiestaDTO, inquiries: TipoDomandaDTO[]) {
    return !inquiries.find(
      inquiry =>
        inquiry.idGruppo === submission.idGruppo && inquiry.flRichiestaUnivoca,
    );
  }

  /**
   * Ritorna vero se la richiesta è annullabile, ovvero se il flag di
   * annullabilità è attivo, la richiesta non è già annullata e non è
   * già in corso un annullamento
   */
  static canUndo(submission?: Pick<RichiestaDTO, 'flAnnullabile' | 'stato'>) {
    return (
      submission?.flAnnullabile &&
      !SubmissionLogic.isSubmissionUndo(submission) &&
      !SubmissionLogic.isSubmissionUndoInProgress(submission)
    );
  }

  /**
   * Ritorna vero se la richiesta è una integrazione a un'altra richiesta
   */
  static isIntegration(submission: RichiestaDTO | null | undefined) {
    if (!submission) return false;
    return submission.richiestaOriginale != null;
  }

  /**
   * Se siamo in un'integrazione, ritorna la funzione che controlla
   * quando un widget è integrabile o meno (utile per mettere il widget
   * in readonly).
   *
   * Se ci troviamo nella richiesta principale, ritorna null (i widget non devono
   * prendere in considerazione la direttiva di integrabilità.)
   */
  static getIsWidgetReadonlyFunction(
    submission: RichiestaDTO | null | undefined,
  ) {
    // Se non è un'integrazione ritorna undefined
    if (!SubmissionLogic.isIntegration(submission)) return undefined;
    return (widget: Widget) => !isWidgetIntegrable(widget);
  }

  /**
   * Ritorna vero se la richiesta è in stato di pagamento
   */
  static isSubmissionInPayment(
    submission: Pick<RichiestaDTO, 'stato'> | null | undefined,
  ) {
    return submission?.stato === EnumRichiestaDTOStato.PAGAMENTO;
  }

  /**
   * Ritorna vero se la richiesta è stata già inviata.
   * Utilizzato per il redirect alla pagina "inviata"
   */
  static isSubmissionSent(
    submission: Pick<RichiestaDTO, 'stato'> | null | undefined,
  ) {
    return (
      submission?.stato === EnumRichiestaDTOStato.INVIATA ||
      submission?.stato === EnumRichiestaDTOStato.RICEVUTA ||
      submission?.stato === EnumRichiestaDTOStato.INTEGRATA ||
      submission?.stato === EnumRichiestaDTOStato.CONCLUSA ||
      submission?.stato === EnumRichiestaDTOStato.ANNULLATA ||
      submission?.stato === EnumRichiestaDTOStato.ANNULLAMENTO_IN_CORSO
    );
  }

  /**
   * Ritorna vero se la richiesta è stata annullata
   */
  static isSubmissionUndo(
    submission: Pick<RichiestaDTO, 'stato'> | null | undefined,
  ) {
    return submission?.stato === EnumRichiestaDTOStato.ANNULLATA;
  }

  /**
   * Ritorna vero se per la richiesta è in corso il flusso di annullamento
   * gestito dal workflow (richiesta congelata in attesa dell'esito)
   */
  static isSubmissionUndoInProgress(
    submission: Pick<RichiestaDTO, 'stato'> | null | undefined,
  ) {
    return submission?.stato === EnumRichiestaDTOStato.ANNULLAMENTO_IN_CORSO;
  }

  /**
   * Ritorna vero se la richiesta è IN_INVIO, ovvero non è modificabile, ma
   * non è nemmeno inviata.
   */
  static isSubmissionPending(submission: RichiestaDTO | null | undefined) {
    return submission?.stato === EnumRichiestaDTOStato.IN_INVIO;
  }

  /**
   * Ritorna vero se la richiesta è stata appena creata
   */
  static isSubmissionNew(submission: RichiestaDTO | null | undefined) {
    return submission?.stato === EnumRichiestaDTOStato.CREATA;
  }

  /**
   * Se la richiesta è in pagamento o è inviata, il modulo è in sola lettura
   */
  static isSubmissionReadonly(submission: RichiestaDTO | null | undefined) {
    return (
      SubmissionLogic.isSubmissionSent(submission) ||
      SubmissionLogic.isSubmissionInPayment(submission)
    );
  }

  /**
   * Ritorna vero se l'utente è il richiedente (autore) della richiesta
   */
  static isAuthor(
    currentUser: DatiSessioneUtente | null,
    submission: RichiestaDTO | null | undefined,
  ) {
    if (!currentUser || !submission) return false;
    return submission?.idRichiedente === currentUser?.utenteLoggato?.idUtente;
  }

  /**
   * Dopo le modifiche alla gestione dei soggetti, il BE restituisce in datiTitolari anche
   * _tutti i dati dei soggetti_ (viewer/editor). Per evitare che questa breaking change impatti
   * sulle logiche di FE, andiamo a filtrare il campo `datiTitolari` per rimuovere tutti i
   * soggetti che non sono titolari.
   *
   * Questa modifica in place l'array dei soggetti.
   */
  static filterSubmissionApplicantData(
    submission: RichiestaDTO | null | undefined,
  ): RichiestaDTO | null | undefined {
    if (!submission) return submission;
    submission.datiTitolari = submission.datiTitolari?.filter(
      t => t.tipologia === EnumDatiTitolareTipologia.TITOLARE,
    );

    return submission;
  }

  /**
   * Come per la `filterSubmissionApplicantData`, ma per un array di richieste.
   */
  static filterSubmissionsApplicantData(
    submissions: RichiestaDTO[] | null | undefined,
  ): RichiestaDTO[] {
    return (submissions
      ?.map(s => SubmissionLogic.filterSubmissionApplicantData(s))
      .filter(Boolean) ?? []) as RichiestaDTO[];
  }

  /**
   * Ritorna vero se la richiesta può essere delegata, e quindi possiamo
   * mostrare il pulsante di delega.
   */
  static canViewDelegationPage(props: {
    submission: RichiestaDTO | undefined | null;
    inquiryType: TipoDomandaDTO | undefined | null;
    canViewDelegates: boolean;
  }) {
    const { submission, inquiryType, canViewDelegates } = props;

    // Mostro il pulsante di delega se la richiesta non è stata inviata e non è già delegata
    // oltre a verificare se la richiesta permette la delega
    return (
      !SubmissionLogic.isSubmissionSent(submission) &&
      (Boolean(inquiryType?.permetteDelega) ||
        Boolean(inquiryType?.flProcuraEsplicita)) &&
      canViewDelegates &&
      !isInquiryReadonlyPreview(inquiryType)
    );
  }
}
